import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

const VARIANTE: Record<Variant, string> = {
  // `text-background`, no `text-white`: sobre el ámbar de marca el blanco da
  // 2,15:1 y deja el botón principal ilegible. El casi negro del fondo da
  // 9,2:1. `contrast.test.ts` lo mide en cada `pnpm test`.
  primary: 'bg-brand text-background shadow-glow hover:brightness-110',
  secondary: 'bg-surface-2 text-content hover:bg-surface-2/70 border border-line-strong/60',
  ghost: 'text-muted hover:bg-surface-2/60 hover:text-content',
  // `danger-strong`, no `danger`: el rojo claro es el de texto y deja el
  // blanco en 2,4:1. Ver el bloque de tokens en `styles.css`.
  danger: 'bg-danger-strong text-white hover:brightness-110',
  outline: 'border border-line-strong text-content hover:border-primary hover:text-primary-soft',
};

const TAMANO: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

/**
 * `min-w-[1.5rem]` cubre 2.5.8 «Target Size (Minimum)», nuevo en WCAG 2.2: un
 * botón de sólo icono no puede quedar por debajo de 24×24 px. Las tres alturas
 * ya lo cumplen; el ancho es el que se colapsa cuando el contenido es «✕».
 */
const BASE =
  'inline-flex min-w-[1.5rem] items-center justify-center rounded-xl font-medium transition ' +
  'whitespace-nowrap disabled:cursor-not-allowed ' +
  // El deshabilitado NO se hace con opacidad. `opacity-50` atenúa texto y
  // fondo a la vez: el blanco sobre marca cae a 2,99:1 y deja de leerse. Con
  // colores propios el estado se distingue igual y el texto sigue legible.
  'disabled:bg-none disabled:bg-surface-2 disabled:text-muted ' +
  'disabled:border-line disabled:shadow-none disabled:hover:brightness-100';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  /** Renderiza un enlace de router con el aspecto de botón. */
  to?: string;
  /** Enlace externo. Se abre en pestaña nueva con `rel` seguro. */
  href?: string;
}

/**
 * El aspecto de un botón, sin ser un `<button>`.
 *
 * Existe para el único caso en el que no puede serlo: la etiqueta que abre el
 * selector de archivos. Ahí hace falta un `<label>` de verdad —es lo que abre
 * el selector sin depender de un `click()` por JavaScript sobre un `<input>`
 * escondido, que es donde algunos navegadores de móvil se plantan—, y sin esto
 * habría que copiar las clases y quedarían dos aspectos de botón que se separan
 * al primer retoque.
 */
export function botonClases(
  opciones: { variant?: Variant; size?: Size; className?: string } = {},
): string {
  const { variant = 'primary', size = 'md', className } = opciones;
  return cn(BASE, VARIANTE[variant], TAMANO[size], className);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, to, href, className, children, ...rest },
  ref,
) {
  const clases = botonClases({ variant, size, className });

  if (to) {
    return (
      <Link to={to} className={clases}>
        {icon}
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" className={clases}>
        {icon}
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      className={clases}
      // Un botón ocupado sigue siendo enfocable, pero no se puede activar dos
      // veces: `aria-busy` lo cuenta a quien no ve el spinner.
      aria-busy={loading || undefined}
      disabled={loading || rest.disabled}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z"
      />
    </svg>
  );
}
